import { useEffect, useState, type ReactNode } from 'react';
import { OptionChip, StudentWorkImages } from '../components';
import type { StudentWork, OperationQuestion } from './types';

/** Fetch the byte size of a blob URL and expose it in KB. Uses a HEAD request
 *  first (cheap, returns Content-Length); falls back to a full GET → blob.size
 *  if HEAD doesn't surface the header. Returns null while loading or on error. */
function useImageSizeKB(url: string | undefined | null): number | null {
    const [sizeKB, setSizeKB] = useState<number | null>(null);
    useEffect(() => {
        if (!url) { setSizeKB(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const head = await fetch(url, { method: 'HEAD' });
                const len = head.headers.get('content-length');
                if (len) {
                    if (!cancelled) setSizeKB(Math.round(parseInt(len, 10) / 1024));
                    return;
                }
                // Fallback: reuses the browser's image cache from the <img> tag.
                const res = await fetch(url);
                const blob = await res.blob();
                if (!cancelled) setSizeKB(Math.round(blob.size / 1024));
            } catch {
                if (!cancelled) setSizeKB(null);
            }
        })();
        return () => { cancelled = true; };
    }, [url]);
    return sizeKB;
}

/**
 * Collapsible card showing the student's work — MCQ selection, canvas
 * drawing (with optional replay), and/or typed answer.
 *
 * `work` is structurally typed (see `StudentWork`) so callers can pass
 * a Submission / HintRequestDoc / ConceptLab record directly without
 * projecting fields one-by-one. Adding a new visible field is a
 * one-place change on `StudentWork`.
 *
 * See docs/FeatureDesigns/operation-detail-unified-layout.md.
 */
export interface StudentWorkCardProps {
    work?: StudentWork;
    /** Used to resolve MCQ option label → chip text. */
    questionOptions?: OperationQuestion['options'];
    title?: string;
    /** Start collapsed. */
    defaultCollapsed?: boolean;
    /** Click handler for the "Replay Drawing" link. Only rendered when the
     *  card is showing a canvas drawing (inputMode === 'draw'); ignored for
     *  photo uploads since strokes weren't recorded. */
    onReplay?: () => void;
    /** Controls that act on the IMAGE rather than on the reading of it —
     *  "this work belongs to another question". Rendered on the image's label
     *  row, and above the body when there is no image: an absent image is
     *  itself a reason to question the region. */
    actions?: ReactNode;
}

export function StudentWorkCard({
    work,
    questionOptions,
    title = 'Student Solution',
    defaultCollapsed = false,
    onReplay,
    actions,
}: StudentWorkCardProps) {
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    // Prefer the pre-captured size on the work record (set at upload time);
    // fall back to a fetch hook only for legacy rows where it's missing.
    const fetchedSizeKB = useImageSizeKB(
        work?.canvasImageSizeBytes !== undefined ? null : work?.canvasImageUrl,
    );
    const canvasSizeKB = work?.canvasImageSizeBytes !== undefined
        ? Math.round(work.canvasImageSizeBytes / 1024)
        : fetchedSizeKB;

    const hasAnything = !!(work?.canvasImageUrl || work?.typedText || work?.selectedOption);

    return (
        <div className="card solution-card">
            <button className="card-toggle" onClick={() => setCollapsed(c => !c)}>
                <span className={`toggle-icon ${collapsed ? 'collapsed' : ''}`}>▼</span>
                <h3>{title}</h3>
            </button>
            {!collapsed && (
                <div className="solution-content">
                    {actions && !work?.canvasImageUrl && (
                        <div className="canvas-label-row">{actions}</div>
                    )}
                    {work?.selectedOption && (
                        <div className="mcq-answer">
                            <label>Selected Option:</label>
                            {(() => {
                                const opt = questionOptions?.find(
                                    o => o.label?.toLowerCase() === work.selectedOption!.toLowerCase(),
                                );
                                const label = opt?.label || work.selectedOption;
                                return <OptionChip label={label} value={opt?.text} color="accent" />;
                            })()}
                        </div>
                    )}
                    {work?.canvasImageUrl && (() => {
                        // Photo uploads also surface via canvasImageUrl, but
                        // they aren't strokes — replay only makes sense for
                        // 'draw'. Label follows the same split so a photo
                        // doesn't get mis-labeled "Canvas Drawing".
                        const isPhoto = work.inputMode === 'photo';
                        const labelText = isPhoto ? 'Photo' : 'Canvas Drawing';
                        const sizeSuffix = canvasSizeKB !== null ? ` (${canvasSizeKB} KB)` : '';
                        const showReplay = !isPhoto && !!onReplay;
                        return (
                            <div className="canvas-section">
                                <div className="canvas-label-row">
                                    <label>{labelText}{sizeSuffix}:</label>
                                    {showReplay && (
                                        <button className="replay-link" onClick={onReplay}>
                                            Replay Drawing
                                        </button>
                                    )}
                                    {actions}
                                </div>
                                <div className="canvas-wrapper">
                                    <StudentWorkImages
                                        imageUrl={work.canvasImageUrl}
                                        pageUrls={work.solutionPageUrls}
                                        alt="Student's solution"
                                        className="canvas-image"
                                    />
                                </div>
                            </div>
                        );
                    })()}
                    {work?.typedText && (
                        <div className="typed-section">
                            <label>Typed Answer:</label>
                            <pre className="typed-text">{work.typedText}</pre>
                        </div>
                    )}
                    {!hasAnything && (
                        <p className="no-input">No solution input recorded</p>
                    )}
                </div>
            )}
        </div>
    );
}
